import { accountsSignInUrl } from "../api/accounts";
import { useEffect, useRef, useState } from "react";
import { ChevronDown, CircleHelp, Feather, Globe, KeyRound, LogOut, MessageCircle, Moon, Settings2, Sun, UserRound } from "lucide-react";

import { IconButton } from "./IconButton";
import { loadChatUnread, loadStoryAccount, signOutStoryAccount, storyAccountsOrigin, storyChatOrigin, type StoryAccount } from "../api/account";
import { storyLocales, useStoryPreferences, type StoryLocale } from "../i18n";

type TopBarProps = {
  head: string;
  projectName?: string;
  branchName?: string;
  branches?: string[];
  connected?: boolean;
  onBranchChange?: (name: string) => void;
  onCreateBranch?: () => void;
};

export function TopBar({ head, projectName = "雾港来信", branchName = "main", branches = [branchName], connected = false, onBranchChange, onCreateBranch }: TopBarProps) {
  const { locale, setLocale, theme, toggleTheme, t } = useStoryPreferences();
  const [account, setAccount] = useState<StoryAccount | null>(null);
  const [accountChecked, setAccountChecked] = useState(false);
  const [chatUnread, setChatUnread] = useState(0);
  const [localeOpen, setLocaleOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const actionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let active = true;
    setAccountChecked(false);
    loadStoryAccount(locale).then((value) => {
      if (active) setAccount(value);
    }).catch(() => {
      if (active) setAccount(null);
    }).finally(() => {
      if (active) setAccountChecked(true);
    });
    return () => { active = false; };
  }, [locale]);

  useEffect(() => {
    if (!account) return;
    let active = true;
    let requestInFlight = false;
    const refresh = async () => {
      if (document.visibilityState !== "visible" || requestInFlight) return;
      requestInFlight = true;
      try {
        const count = await loadChatUnread();
        if (active) setChatUnread(count);
      } catch {
        // Chat availability must never block the editor shell.
      } finally {
        requestInFlight = false;
      }
    };
    void refresh();
    const timer = window.setInterval(refresh, 45_000);
    const onFocus = () => void refresh();
    window.addEventListener("focus", onFocus);
    return () => {
      active = false;
      window.clearInterval(timer);
      window.removeEventListener("focus", onFocus);
    };
  }, [account]);

  useEffect(() => {
    const closeMenus = (event: PointerEvent) => {
      if (!actionsRef.current?.contains(event.target as Node)) {
        setLocaleOpen(false);
        setAccountOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setLocaleOpen(false);
        setAccountOpen(false);
      }
    };
    document.addEventListener("pointerdown", closeMenus);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeMenus);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, []);

  const localeNames: Record<StoryLocale, string> = { "zh-Hans": "简体中文", "zh-Hant": "繁體中文", en: "English", ja: "日本語", ko: "한국어" };
  const displayName = account?.user.name?.trim() || account?.user.email?.split("@")[0] || t("account");
  const initials = displayName.slice(0, 2).toUpperCase();
  const profileHref = (section: string) => `${storyAccountsOrigin}/profile?locale=${locale}&section=${section}`;

  async function handleSignOut() {
    await signOutStoryAccount().catch(() => undefined);
    window.location.replace(`/${window.location.search}${window.location.hash}`);
  }

  return (
    <header className="top-bar">
      <a className="brand" href="#write" aria-label="xHalo Story"><Feather aria-hidden="true" /><span>xHalo Story</span></a>
      <div className="project-context" aria-label="当前项目和分支">
        <span>{projectName}</span><span className="slash">/</span>
        {onBranchChange ? (
          <label className="branch-select-label">
            <span className="sr-only">{t("currentBranch")}</span>
            <select aria-label={t("currentBranch")} value={branchName} onChange={(event) => onBranchChange(event.target.value)}>
              {branches.map((name) => <option key={name} value={name}>{name}</option>)}
            </select>
            <ChevronDown size={15} strokeWidth={1.7} aria-hidden="true" />
          </label>
        ) : <><span>{branchName}</span><ChevronDown size={15} strokeWidth={1.7} /></>}
        {onCreateBranch ? <button type="button" className="branch-add" onClick={onCreateBranch} aria-label={t("createBranch")}>+</button> : null}
        <span className="slash desktop-only">/</span><span className="desktop-only">{head}</span>
        {connected ? <span className="api-connected desktop-only">API</span> : null}
      </div>
      <div className="top-actions" ref={actionsRef}>
        <div className="top-menu">
          <button type="button" className="top-locale-trigger" aria-label={t("language")} aria-haspopup="menu" aria-expanded={localeOpen} onClick={() => { setLocaleOpen((open) => !open); setAccountOpen(false); }}><Globe aria-hidden="true" /><span>{localeNames[locale]}</span><ChevronDown aria-hidden="true" /></button>
          {localeOpen ? <div className="top-dropdown locale-dropdown" role="menu" aria-label={t("language")}>{storyLocales.map((value) => <button type="button" role="menuitemradio" aria-checked={value === locale} className={value === locale ? "is-selected" : ""} key={value} onClick={() => { setLocale(value); setLocaleOpen(false); }}>{localeNames[value]}</button>)}</div> : null}
        </div>
        {account ? <a className="chat-button" href={storyChatOrigin} target="_blank" rel="noopener noreferrer" aria-label="xHalo Chat" title="xHalo Chat"><MessageCircle aria-hidden="true" />{chatUnread > 0 ? <span aria-label={`${chatUnread} unread messages`}>{chatUnread >= 100 ? "99+" : chatUnread}</span> : null}</a> : null}
        <IconButton icon={theme === "dark" ? Sun : Moon} label={t("appearance")} onClick={toggleTheme} />
        {account ? <div className="top-menu">
          <button type="button" className="account-trigger" aria-label={t("account")} aria-haspopup="menu" aria-expanded={accountOpen} onClick={() => { setAccountOpen((open) => !open); setLocaleOpen(false); }}>{account.user.image ? <img src={account.user.image} alt="" /> : <span>{initials}</span>}</button>
          {accountOpen ? <div className="top-dropdown account-dropdown" role="menu">
            <header><small>{t("signedInAs")}</small><strong>{displayName}</strong><span>{account.user.email}</span></header>
            <a role="menuitem" href={profileHref("profile")}><UserRound aria-hidden="true" />{t("profile")}</a>
            <a role="menuitem" href={profileHref("settings")}><Settings2 aria-hidden="true" />{t("settings")}</a>
            <a role="menuitem" href={profileHref("language")}><Globe aria-hidden="true" />{t("language")}</a>
            <a role="menuitem" href={profileHref("developers")}><KeyRound aria-hidden="true" />{t("developers")}</a>
            <a role="menuitem" href={profileHref("help")}><CircleHelp aria-hidden="true" />{t("help")}</a>
            <button type="button" role="menuitem" className="sign-out" onClick={() => void handleSignOut()}><LogOut aria-hidden="true" />{t("signOut")}</button>
          </div> : null}
        </div> : accountChecked ? <a className="account-sign-in" href={accountsSignInUrl(locale)}>{t("signIn")}</a> : <span className="account-skeleton" aria-hidden="true" />}
      </div>
    </header>
  );
}
